import { EidEasySignStatus } from '@dealstack/frontend/shared/components/EidEasySigningModal/EidEasySigningModal.types';
import { DocumentWorkflowInstanceStatus } from '@dealstack/shared/lib/generated';

const IN_FLIGHT_STATUSES = new Set<EidEasySignStatus>([
	EidEasySignStatus.PREPARING,
	EidEasySignStatus.AWAITING_SIGN,
	EidEasySignStatus.COMPLETING,
]);

const SUB_STEP_STATUSES = new Set<EidEasySignStatus>([
	EidEasySignStatus.SETUP_SIGNATURE,
	EidEasySignStatus.SMART_ID_INPUT,
	EidEasySignStatus.DRAW_SIGNATURE,
	EidEasySignStatus.OTP_INPUT,
	EidEasySignStatus.OTP_CODE_ENTRY,
]);

export enum SignersTabView {
	LIST = 'list',
	SIGNING = 'signing',
}

export interface EidEasySigningPhase {
	view: SignersTabView;
	canGoBack: boolean;
	isOnSubStep: boolean;
}

export function getEidEasySigningPhase(status: EidEasySignStatus): EidEasySigningPhase {
	return {
		view: status === EidEasySignStatus.IDLE ? SignersTabView.LIST : SignersTabView.SIGNING,
		canGoBack: !IN_FLIGHT_STATUSES.has(status),
		isOnSubStep: SUB_STEP_STATUSES.has(status),
	};
}

export interface ApplyDateAction {
	visible: boolean;
	enabled: boolean;
}

// Mirrors UNDATABLE_INSTANCE_STATUSES in documentWorkflowInstance/repository.ts — statuses an
// instance cannot reach COMPLETED from without being reissued first. Duplicated rather than
// shared: that constant is typed on the Prisma enum and lives in boot-graph code, which cannot
// runtime-import the generated models this file uses.
const UNDATABLE_WORKFLOW_STATUSES = new Set<DocumentWorkflowInstanceStatus>([
	DocumentWorkflowInstanceStatus.CANCELLED,
	DocumentWorkflowInstanceStatus.ERROR,
	DocumentWorkflowInstanceStatus.EXPIRED,
	DocumentWorkflowInstanceStatus.REJECTED,
]);

/**
 * Entry point for post-dating. Shown from issuance so the step is discoverable, enabled only on
 * COMPLETED — the status the backend sets on the last signature and the one it requires. Hidden
 * outright on a terminal status, where enabling it could never happen.
 */
export function getApplyDateAction({
	requiresPostDating,
	dateAppliedAt,
	workflowStatus,
}: {
	requiresPostDating: boolean;
	dateAppliedAt: string | null;
	workflowStatus: DocumentWorkflowInstanceStatus;
}): ApplyDateAction {
	return {
		visible:
			requiresPostDating &&
			dateAppliedAt === null &&
			!UNDATABLE_WORKFLOW_STATUSES.has(workflowStatus),
		enabled: workflowStatus === DocumentWorkflowInstanceStatus.COMPLETED,
	};
}
